import { TableColumnDef } from '@melio/penny';
import { Payment } from '@melio/platform-api';
import { useMelioIntl } from '@melio/platform-i18n';
import { useMemo } from 'react';

import { useTableColumnsSort } from '../../hooks/useTableColumnsSort';
import { usePayDashboardSorting } from '../../PayDashboardSortingProvider';
import { BillsTabOriginCell } from '../BillsTab/components/BillsTabOriginCell';
import { PaymentsTabAmountCell } from '../PaymentsTab/components/PaymentsTabAmountCell';
import { PaymentsTabDebitDateCell } from '../PaymentsTab/components/PaymentsTabDebitDateCell';
import { PaymentsTabScheduledByCell } from '../PaymentsTab/components/PaymentsTabScheduledByCell';
import { PaymentsTabStatusCell } from '../PaymentsTab/components/PaymentsTabStatusCell';
import { PaymentsTabVendorNameCell } from '../PaymentsTab/components/PaymentsTabVendorNameCell';
import { ApprovalsTabActionButtonCell } from './components/ApprovalsTabActionButtonCell';
import { ApprovalsTabSortableCells, CELLS_IDS, PaymentsTableMeta } from './types';
import { useApprovalsTabSortableColumns } from './useApprovalsTabSortableColumns';

export const useApprovalsTableColumns = () => {
  const { formatMessage } = useMelioIntl();
  const sorting = usePayDashboardSorting();
  const { getSortableHeader } = useTableColumnsSort<ApprovalsTabSortableCells>({ sorting });
  const { sortableColumns } = useApprovalsTabSortableColumns();

  return useMemo<TableColumnDef<Payment, PaymentsTableMeta>[]>(() => {
    const vendorCell: TableColumnDef<Payment, PaymentsTableMeta> = {
      id: CELLS_IDS.VENDOR_NAME,
      size: 's',
      header: getSortableHeader(sortableColumns[CELLS_IDS.VENDOR_NAME]),
      cell: ({ row, meta }) =>
        meta ? (
          <PaymentsTabVendorNameCell
            payment={row}
            onEBillSubscribeClick={meta.onEBillSubscribeClick}
            onAutoPayActivationClick={meta.onAutoPayActivationClick}
          />
        ) : null,
    };
    const scheduledByCell: TableColumnDef<Payment, PaymentsTableMeta> = {
      id: CELLS_IDS.SCHEDULED_BY,
      size: 's',
      header: formatMessage('activities.payDashboard.paymentsTab.table.headers.scheduledBy'),
      cell: ({ row }) => <PaymentsTabScheduledByCell payment={row} />,
    };
    const billCell: TableColumnDef<Payment, PaymentsTableMeta> = {
      id: CELLS_IDS.BILL,
      size: 's',
      header: formatMessage('activities.payDashboard.paymentsTab.table.headers.bill'),
      cell: ({ row }) =>
        row.bills?.[0] ? <BillsTabOriginCell billsTabItem={{ type: 'bill', payload: row.bills[0] }} /> : null,
    };
    const debitDateCell: TableColumnDef<Payment, PaymentsTableMeta> = {
      id: CELLS_IDS.DEBIT_DATE,
      size: 's',
      header: getSortableHeader(sortableColumns[CELLS_IDS.DEBIT_DATE]),
      cell: ({ row }) => <PaymentsTabDebitDateCell payment={row} />,
    };
    const statusCell: TableColumnDef<Payment, PaymentsTableMeta> = {
      id: CELLS_IDS.STATUS,
      size: 's',
      header: formatMessage('activities.payDashboard.paymentsTab.table.headers.status'),
      cell: ({ row }) => <PaymentsTabStatusCell payment={row} />,
    };
    const amountCell: TableColumnDef<Payment, PaymentsTableMeta> = {
      id: CELLS_IDS.AMOUNT,
      size: 200,
      header: getSortableHeader({
        textAlign: 'end',
        ...sortableColumns[CELLS_IDS.AMOUNT],
      }),
      textAlign: 'end',
      cell: ({ row }) => <PaymentsTabAmountCell payment={row} />,
    };
    const actionButtonColumn: TableColumnDef<Payment, PaymentsTableMeta> = {
      id: CELLS_IDS.ACTION_BUTTON,
      size: 140,
      cell: ({ row, meta }) =>
        meta ? (
          <ApprovalsTabActionButtonCell payment={row} onApprovalDecisionClick={meta.onApprovalDecisionClick} />
        ) : null,
    };

    return [vendorCell, scheduledByCell, billCell, debitDateCell, statusCell, amountCell, actionButtonColumn];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formatMessage, getSortableHeader]);
};
